import { useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles, Loader2 } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

const GREETING_VI = (
  "Xin chào! Tôi là AsterPOS AI Business Advisor của Nail Viet Club.\n\n" +
  "Bạn muốn làm gì hôm nay?\n\n" +
  "1. Tôi muốn đăng ký tham dự sự kiện\n" +
  "2. Tôi muốn tổ chức một sự kiện\n" +
  "3. Tôi cần hỏi về kinh doanh tiệm nail / beauty\n" +
  "4. Tôi cần hỗ trợ check-in hoặc đăng ký"
);

const GREETING_EN = (
  "Hello! I'm the AsterPOS AI Business Advisor for Nail Viet Club.\n\n" +
  "How can I help today?\n\n" +
  "1. I want to register for an event\n" +
  "2. I want to host an event\n" +
  "3. I have a nail/beauty business question\n" +
  "4. I need help with check-in or registration"
);

const QUICK_ACTIONS = {
  vi: [
    { id: "register",   label: "Đăng ký sự kiện",       intent: "Tôi muốn đăng ký tham dự một sự kiện." },
    { id: "host",       label: "Tổ chức sự kiện",       intent: "Tôi muốn tổ chức một sự kiện. Hãy giúp tôi tạo tài khoản tổ chức." },
    { id: "business",   label: "Hỏi về kinh doanh",     intent: "Tôi có câu hỏi về kinh doanh tiệm nail." },
    { id: "checkin",    label: "Hỗ trợ check-in",       intent: "Tôi cần hỗ trợ check-in tại sự kiện." },
  ],
  en: [
    { id: "register",   label: "Register for event",    intent: "I want to register for an event." },
    { id: "host",       label: "Host an event",         intent: "I want to host an event. Please help me create an organizer account." },
    { id: "business",   label: "Business question",     intent: "I have a nail salon business question." },
    { id: "checkin",    label: "Check-in help",         intent: "I need help with check-in at an event." },
  ],
};

const SESSION_KEY = "nailEvent.aiSession";


export default function AiAdvisorPage() {
  const { lang } = useI18n();
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    setMessages([{
      role: "assistant",
      content: lang === "en" ? GREETING_EN : GREETING_VI,
    }]);
  }, [lang]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  const send = async (raw) => {
    const msg = (raw ?? text).trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setText("");
    setSending(true);
    try {
      const r = await api.post("/ai/chat", { message: msg, session_id: sessionId || undefined, language: lang });
      if (r.data.session_id && !sessionId) { setSessionId(r.data.session_id); sessionStorage.setItem(SESSION_KEY, r.data.session_id); }
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: lang === "en" ? "Sorry, I'm temporarily unavailable. Please try again." : "Xin lỗi, AI tạm gián đoạn. Vui lòng thử lại." }]);
    } finally { setSending(false); }
  };

  const handleKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="min-h-screen bg-white text-charcoal flex flex-col" data-testid="ai-page">
      <SiteHeader variant="light" />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 md:px-8 py-8 md:py-10 flex flex-col">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-charcoal-900 text-gold-500 flex items-center justify-center"><Sparkles className="w-5 h-5" /></div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-emerald2 font-semibold">{lang === "en" ? "Powered by AsterPOS Corp" : "Được cung cấp bởi AsterPOS Corp"}</p>
            <h1 className="font-serif text-3xl md:text-4xl text-charcoal leading-none mt-1">AsterPOS AI Business Advisor</h1>
          </div>
        </div>

        <div ref={scrollerRef} className="flex-1 bg-warmgray-50 border border-warmgray-200 rounded-3xl p-4 md:p-6 overflow-y-auto min-h-[480px] max-h-[60vh] space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-page-msg-${i}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-emerald2 text-white" : "bg-white border border-warmgray-200 text-charcoal"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-warmgray-200 rounded-2xl px-4 py-2.5 text-sm text-warmgray-700 inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {lang === "en" ? "Thinking..." : "Đang suy nghĩ..."}
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="ai-quick-actions">
          {(QUICK_ACTIONS[lang] || QUICK_ACTIONS.vi).map((q) => (
            <button key={q.id} type="button" onClick={() => send(q.intent)} data-testid={`ai-quick-${q.id}`}
              className="text-xs border border-warmgray-300 rounded-full px-3 py-1.5 text-warmgray-700 hover:text-charcoal hover:border-emerald2 transition-all">
              {q.label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-end gap-2">
          <textarea data-testid="ai-page-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={handleKey} rows={1}
            placeholder={lang === "en" ? "Ask anything — business, taxes, HR, marketing, AsterPOS..." : "Hỏi bất cứ điều gì — kinh doanh, thuế, nhân sự, marketing, AsterPOS..."}
            className="flex-1 resize-none bg-white border border-warmgray-300 rounded-2xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald2/40 max-h-32" />
          <button type="button" onClick={() => send()} disabled={sending || !text.trim()} data-testid="ai-page-send"
            className="bg-emerald2 text-white rounded-full w-12 h-12 flex items-center justify-center disabled:opacity-50 hover:bg-emerald2-deep transition-all">
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-3 text-xs text-warmgray-500">
          {lang === "en"
            ? "Educational information only. For situation-specific legal or tax advice, consult a licensed professional."
            : "Chỉ mang tính giáo dục. Với tình huống cụ thể về thuế hoặc pháp lý, vui lòng liên hệ chuyên gia có giấy phép."}
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
